import {
  fetchUtils,
  GetOneParams,
  GetOneResult,
  HttpError,
  Identifier,
  RaRecord,
} from 'react-admin';
import { defaultHeaders } from './utils';
import {
  JsonApiResource,
  JsonApiSingleResponse,
  createReactAdminValidationError,
  getJsonApiErrorBody,
  getJsonApiErrorMessage,
  getJsonApiErrorStatus,
} from './JsonApiTypes';

export default function getOne(apiUrl: string, httpClient = fetchUtils.fetchJson) {
  return async <T extends RaRecord<Identifier>>(
    resource: string,
    params: GetOneParams,
  ): Promise<GetOneResult<T & JsonApiResource>> => {
    const query = new URLSearchParams();

    params.meta?.prefetch?.forEach((prefetch: string) => {
      query.append('include', prefetch);
    });

    const url = `${apiUrl}/${resource}/${params.id}?${query}`;

    try {
      const response = await httpClient(url, { headers: defaultHeaders, signal: params.signal });
      const result = response.json as JsonApiSingleResponse;

      const prefetched = result.included?.reduce(
        (acc, includedRecord) => {
          acc[includedRecord.type] ??= [];
          acc[includedRecord.type].push(includedRecord);
          return acc;
        },
        {} as Record<string, JsonApiResource[]>,
      );

      return { data: result.data as T & JsonApiResource, meta: { prefetched } };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }

      const validationErrors = createReactAdminValidationError(error as HttpError);
      throw new HttpError(
        getJsonApiErrorMessage(error as HttpError, 'Error fetching record'),
        getJsonApiErrorStatus(error as HttpError),
        {
          ...validationErrors,
          apiResponse: getJsonApiErrorBody(error),
        },
      );
    }
  };
}
